/**
 * ICONIC: Protokoll-Zeilen in Klartext (Weboberfläche und TeamSpeak-Server-Log).
 */

const q = (s) => `«${s}»`;
const who = (name) => (name === "Web-Admin" || /^serveradmin\d*$/.test(name) ? "Weboberfläche" : name);
const maskIp = (s) =>
  String(s)
    .replace(/\b\d{1,3}(\.\d{1,3}){3}(:\d+)?\b/g, "‹IP›")
    .replace(/\[[0-9a-f:]+\](:\d+)?/gi, "‹IP›");

export function duration(seconds) {
  const n = Number(seconds);
  if (!n) return "für immer";
  if (n % 86400 === 0) return n === 86400 ? "1 Tag" : `${n / 86400} Tage`;
  if (n % 3600 === 0) return n === 3600 ? "1 Stunde" : `${n / 3600} Stunden`;
  if (n % 60 === 0) return `${n / 60} Minuten`;
  return `${n} Sekunden`;
}

/** Eintrag der Weboberfläche → { kind, text } */
export function describeAction(e, lookup) {
  const p = e.params || {};
  const n = e.names || {};
  const person = n.client ? q(n.client) : p.cldbid ? `Benutzer ${p.cldbid}` : "jemand";
  const channel = n.channel ? q(n.channel) : p.cid ? `Kanal ${p.cid}` : "";
  const sg = (id) => q(lookup.sgName(id) || `Rolle ${id}`);
  const cg = (id) => q(lookup.cgLabel(id) || `Kanal-Rolle ${id}`);
  const reason = p.reasonmsg || p.banreason ? ` (Grund: ${p.reasonmsg || p.banreason})` : "";
  switch (e.command) {
    case "clientkick":
      return Number(p.reasonid) === 5
        ? { kind: "kick", text: `${person} vom Server geworfen${reason}` }
        : { kind: "kick", text: `${person} aus dem Kanal geworfen${reason}` };
    case "clientmove":
      return { kind: "kick", text: `${person} nach ${channel} verschoben` };
    case "clientpoke":
      return { kind: "other", text: `${person} angestupst` };
    case "banclient":
      return { kind: "kick", text: `${person} gebannt, ${duration(p.time)}${reason}` };
    case "banadd":
      return { kind: "kick", text: `Bann angelegt, ${duration(p.time)}${reason}` };
    case "bandel":
      return { kind: "kick", text: `Bann ${p.banid} aufgehoben` };
    case "bandelall":
      return { kind: "kick", text: "Alle Banns aufgehoben" };
    case "sendtextmessage":
      return { kind: "other", text: "Nachricht geschickt" };
    case "servergroupaddclient":
      return { kind: "role", text: `${person} bekommt die Rolle ${sg(p.sgid)}` };
    case "servergroupdelclient":
      return { kind: "role", text: `${person} verliert die Rolle ${sg(p.sgid)}` };
    case "setclientchannelgroup":
      return { kind: "role", text: `${person} ist in ${channel} jetzt ${cg(p.cgid)}` };
    case "clientaddperm":
      return { kind: "role", text: `${person}: Einzelrecht ${p.permsid || p.permid} = ${p.permvalue}` };
    case "clientdelperm":
      return { kind: "role", text: `${person}: Einzelrecht ${p.permsid || p.permid} entfernt` };
    case "servergroupcopy":
      return { kind: "role", text: `Rolle ${q(p.name)} angelegt (Vorlage ${sg(p.ssgid)})` };
    case "servergroupdel":
      return { kind: "role", text: `Rolle ${sg(p.sgid)} gelöscht` };
    case "servergrouprename":
      return { kind: "role", text: `Rolle umbenannt in ${q(p.name)}` };
    case "servergroupaddperm":
      return { kind: "role", text: `Rolle ${sg(p.sgid)}: ${p.permsid || p.permid} = ${p.permvalue}` };
    case "servergroupdelperm":
      return { kind: "role", text: `Rolle ${sg(p.sgid)}: ${p.permsid || p.permid} entfernt` };
    case "channelgroupcopy":
      return { kind: "role", text: `Kanal-Rolle ${q(p.name)} angelegt` };
    case "channelgroupaddperm":
      return { kind: "role", text: `Kanal-Rolle ${cg(p.cgid)}: ${p.permsid || p.permid} = ${p.permvalue}` };
    case "channelgroupdelperm":
      return { kind: "role", text: `Kanal-Rolle ${cg(p.cgid)}: ${p.permsid || p.permid} entfernt` };
    case "channelcreate":
      return { kind: "channel", text: `Kanal ${q(p.channel_name)} angelegt` };
    case "channeledit":
      return { kind: "channel", text: `Kanal ${channel} geändert${p.channel_name ? ` (neuer Name ${q(p.channel_name)})` : ""}` };
    case "channeldelete":
      return { kind: "channel", text: `Kanal ${channel} gelöscht` };
    case "channelmove":
      return { kind: "channel", text: `Kanal ${channel} verschoben` };
    case "privilegekeyadd":
    case "tokenadd":
      return { kind: "role", text: `Einladung angelegt${p.tokendescription ? ` für ${q(p.tokendescription)}` : ""}` };
    case "privilegekeydelete":
    case "tokendelete":
      return { kind: "role", text: "Einladung gelöscht" };
    case "serveredit":
      return { kind: "other", text: "Server-Einstellungen geändert" };
    case "clientdbdelete":
      return { kind: "role", text: `${person} aus der Benutzer-Datenbank gelöscht` };
    default:
      return { kind: "other", text: `${e.command}` };
  }
}

/** Zeile aus dem TeamSpeak-Log → { time, kind, text } oder null (Rauschen) */
export function parseLogLine(line) {
  const parts = String(line || "").split("|");
  if (parts.length < 5) return null;
  const time = parts[0].trim();
  const comp = parts[2].trim();
  const msg = parts.slice(4).join("|").trim();
  if (comp === "PktHandler" || /^listening on/.test(msg)) return null;
  let m;
  const out = (kind, text) => ({ time, kind, text });

  if ((m = /^client '(.*)'\(id:\d+\) was added to channelgroup '(.*)'\(id:\d+\) by client '(.*)'\(id:\d+\) in channel '(.*)'\(id:\d+\)$/.exec(msg)))
    return out("role", `${who(m[3])}: ${q(m[1])} ist in ${q(m[4])} jetzt ${q(m[2])}`);
  if ((m = /^client '(.*)'\(id:\d+\) was (added to|removed from) servergroup '(.*)'\(id:\d+\) by client '(.*)'\(id:\d+\)$/.exec(msg)))
    return out("role", `${who(m[4])}: ${q(m[1])} ${m[2] === "added to" ? "bekommt" : "verliert"} die Rolle ${q(m[3])}`);
  if ((m = /^permission '(.*)'\(id:\d+\) with values \((.*)\) was added by '(.*)'\(id:\d+\) to (servergroup|channelgroup) '(.*)'\(id:\d+\)/.exec(msg)))
    return out("role", `${who(m[3])}: ${m[4] === "servergroup" ? "Rolle" : "Kanal-Rolle"} ${q(m[5])}: ${m[1]} (${m[2]})`);
  if ((m = /^permission '(.*)'\(id:\d+\) with values \((.*)\) was added by '(.*)'\(id:\d+\) to client \(id:(\d+)\)/.exec(msg)))
    return out("role", `${who(m[3])}: Benutzer ${m[4]}: Einzelrecht ${m[1]} (${m[2]})`);
  if ((m = /^permission '(.*)'\(id:\d+\) with values \((.*)\) was added by '(.*)'\(id:\d+\) to (.*)$/.exec(msg)))
    return out("role", `${who(m[3])}: ${m[1]} (${m[2]}) für ${maskIp(m[4])}`);
  if ((m = /^permission '(.*)'\(id:\d+\) was deleted by '(.*)'\(id:\d+\) from (servergroup|channelgroup) '(.*)'\(id:\d+\)/.exec(msg)))
    return out("role", `${who(m[2])}: ${m[3] === "servergroup" ? "Rolle" : "Kanal-Rolle"} ${q(m[4])}: ${m[1]} entfernt`);
  if ((m = /^permission '(.*)'\(id:\d+\) was deleted by '(.*)'\(id:\d+\) from (.*)$/.exec(msg)))
    return out("role", `${who(m[2])}: ${m[1]} entfernt bei ${maskIp(m[3])}`);
  if ((m = /^(servergroup|channelgroup) '(.*)'\(id:\d+\) was copied by '(.*)'\(id:\d+\) to '(.*)'\(id:\d+\)/i.exec(msg)))
    return out("role", `${who(m[3])}: ${/^server/i.test(m[1]) ? "Rolle" : "Kanal-Rolle"} ${q(m[4])} angelegt (Kopie von ${q(m[2])})`);
  if ((m = /^(servergroup|channelgroup) '(.*)'\(id:\d+\) was (added|deleted|renamed) by '(.*)'\(id:\d+\)/i.exec(msg)))
    return out("role", `${who(m[4])}: ${/^server/i.test(m[1]) ? "Rolle" : "Kanal-Rolle"} ${q(m[2])} ${{ added: "angelegt", deleted: "gelöscht", renamed: "umbenannt" }[m[3]]}`);
  if ((m = /^channel '(.*)'\(id:\d+\) (created|edited|deleted|moved) by '(.*)'\(id:\d+\)/.exec(msg)))
    return out("channel", `${who(m[3])}: Kanal ${q(m[1])} ${{ created: "angelegt", edited: "geändert", deleted: "gelöscht", moved: "verschoben" }[m[2]]}`);
  if ((m = /^ban (added|deleted) (.*) by client '(.*)'\(id:\d+\)/.exec(msg))) {
    const reason = (/reason='([^']*)'/.exec(m[2]) || [])[1];
    const time = (/bantime=(\d+)/.exec(m[2]) || [])[1];
    const target = (/name='([^']*)'/.exec(m[2]) || [])[1];
    return out("kick", `${who(m[3])}: Bann ${m[1] === "added" ? "angelegt" : "aufgehoben"}${target ? ` für ${q(target)}` : ""}${time !== undefined ? `, ${duration(time)}` : ""}${reason ? ` (Grund: ${reason})` : ""}`);
  }
  if ((m = /^client '(.*)'\(id:\d+\) (added|deleted) privilege key for (servergroup|channelgroup) '(.*)'\(id:\d+\)/.exec(msg)))
    return out("role", `${who(m[1])}: Einladung für ${m[3] === "servergroup" ? "Rolle" : "Kanal-Rolle"} ${q(m[4])} ${m[2] === "added" ? "angelegt" : "gelöscht"}`);
  if ((m = /^client '(.*)'\(id:\d+\) (added|deleted|used) privilege key/.exec(msg)))
    return out("role", `${who(m[1])}: Einladung ${{ added: "angelegt", deleted: "gelöscht", used: "benutzt" }[m[2]]}`);
  if ((m = /^client connected '(.*)'\(id:\d+\)/.exec(msg))) return out("join", `${q(m[1])} ist gekommen`);
  if ((m = /^client disconnected '(.*)'\(id:\d+\) reason '(.*)'$/.exec(msg))) {
    const invoker = (/invokername=(\S+)/.exec(m[2]) || [])[1];
    const reason = (/reasonmsg=(.*?)(?: bantime=|$)/.exec(m[2]) || [])[1];
    const banned = /bantime=/.test(m[2]);
    if (invoker)
      return out("kick", `${who(invoker.replace(/\\s/g, " "))}: ${q(m[1])} ${banned ? "gebannt" : "vom Server geworfen"}${reason ? ` (Grund: ${reason.replace(/\\s/g, " ")})` : ""}`);
    return out("join", `${q(m[1])} ist gegangen`);
  }
  if ((m = /^server was edited by '(.*)'\(id:\d+\)/.exec(msg))) return out("other", `${who(m[1])}: Server-Einstellungen geändert`);
  return out("other", maskIp(msg));
}

export const KINDS = [
  { value: "all", text: "Alles" },
  { value: "role", text: "Rollen & Rechte" },
  { value: "kick", text: "Rauswürfe & Banns" },
  { value: "channel", text: "Kanäle" },
  { value: "join", text: "Kommen & Gehen" },
];
